import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../contexts/ApiContext';
import { WhatsAppCallRecording, recordingSupported } from '../utils/whatsappCallRecording';
import { createRingbackTone } from '../utils/ringbackTone';
import { subscribeWhatsAppCallEvents } from '../utils/whatsappCallEvents';

const WhatsAppCallContext = createContext(null);

// `connected` y el SDP solo indican que WebRTC negocio con WhatsApp. La persona
// contesto unicamente cuando GHL publica accepted (o sus equivalentes finales).
const ANSWERED_STATUSES = new Set(['accepted', 'answered', 'in-progress', 'in_progress']);
const TERMINAL_STATUSES = new Set([
  'completed', 'complete', 'ended', 'terminated', 'failed', 'busy',
  'no-answer', 'no_answer', 'missed', 'rejected', 'cancelled', 'canceled'
]);

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

const statusLabel = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'ringing') return 'Timbrando';
  if (normalized === 'dialing') return 'Marcando';
  if (normalized === 'initiated') return 'Iniciando';
  if (normalized === 'queued') return 'En cola';
  return 'Llamando';
};

const endLabel = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'failed') return 'Llamada fallida';
  if (normalized === 'busy') return 'Linea ocupada';
  if (['no-answer', 'no_answer', 'missed'].includes(normalized)) return 'Sin respuesta';
  if (['rejected', 'cancelled', 'canceled'].includes(normalized)) return 'Llamada cancelada';
  return 'Llamada finalizada';
};

const waitForIceGathering = (peer, timeoutMs = 2500) => {
  if (peer.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      peer.removeEventListener('icegatheringstatechange', onChange);
      resolve();
    };
    const onChange = () => {
      if (peer.iceGatheringState === 'complete') done();
    };
    const timer = setTimeout(done, timeoutMs);
    peer.addEventListener('icegatheringstatechange', onChange);
  });
};

const answerSdp = (result = {}) => (
  result.sdp || result.answer || result.answerSdp || result.data?.sdp ||
  result.data?.answer || result.call?.sdp || ''
);

const phoneCallId = (result = {}) => (
  result.phoneCallId || result.phone_call_id || result.data?.phoneCallId ||
  result.data?.phone_call_id || result.call?.phoneCallId ||
  result.call?.phone_call_id || result.id || ''
);

const callSid = (result = {}) => (
  result.call_id || result.callId || result.call_sid || result.callSid ||
  result.data?.call_id || result.data?.callId || result.data?.call_sid ||
  result.data?.callSid || result.call?.call_id || result.call?.call_sid ||
  result.call?.id || result.calls?.[0]?.id || ''
);

export function WhatsAppCallProvider({ children }) {
  const { whatsapp } = useApi();
  const [display, setDisplay] = useState(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const attemptRef = useRef(0);
  const whatsappRef = useRef(whatsapp);
  const startRecordingRef = useRef(null);

  useEffect(() => { whatsappRef.current = whatsapp; }, [whatsapp]);

  const stopSessionMedia = useCallback((session) => {
    clearInterval(session?.timer);
    session?.ringback?.close();
    if (session) session.ringback = null;
    // Primero la grabacion: entrega su ultimo trozo y sigue enviando la cola
    // aunque la llamada ya haya terminado.
    if (session?.recording) {
      session.recording.stop(session.endReason || 'stop');
      session.recording = null;
    }
    if (session?.peer) {
      session.peer.ontrack = null;
      session.peer.onconnectionstatechange = null;
      session.peer.close();
    }
    session?.stream?.getTracks().forEach((track) => track.stop());
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.srcObject = null;
    }
  }, []);

  const clearSession = useCallback((session, finalLabel = '', isError = false) => {
    if (!session) return;
    session.ending = true;
    if (!session.endReason) session.endReason = isError ? 'failed' : (finalLabel ? 'ended' : 'stop');
    stopSessionMedia(session);
    if (sessionRef.current === session) sessionRef.current = null;
    if (!finalLabel) {
      setDisplay(null);
      return;
    }
    setDisplay((current) => ({
      ...(current || {}),
      phase: isError ? 'failed' : 'ended',
      label: finalLabel,
      elapsedSeconds: session.elapsedSeconds || 0,
      canHangup: false
    }));
    const attemptId = session.attemptId;
    setTimeout(() => {
      if (!sessionRef.current && attemptRef.current === attemptId) setDisplay(null);
    }, 4500);
  }, [stopSessionMedia]);

  const startTimer = useCallback((session) => {
    if (!session || session.timer) return;
    session.startedAt = session.startedAt || Date.now();
    session.timer = setInterval(() => {
      if (sessionRef.current !== session) return;
      session.elapsedSeconds = Math.max(0, Math.floor((Date.now() - session.startedAt) / 1000));
      setDisplay((current) => current ? {
        ...current,
        elapsedSeconds: session.elapsedSeconds
      } : current);
    }, 1000);
  }, []);

  const markAnswered = useCallback((session, acceptedAt = null) => {
    if (!session || session.answeredAt || session.ending) return;
    const parsedAcceptedAt = acceptedAt ? new Date(acceptedAt).getTime() : NaN;
    session.answeredAt = Number.isFinite(parsedAcceptedAt) ? parsedAcceptedAt : Date.now();
    session.startedAt = session.answeredAt;
    session.ringback?.close();
    session.ringback = null;
    startTimer(session);
    startRecordingRef.current?.(session);
    setDisplay((current) => current ? {
      ...current,
      phase: 'active',
      label: 'En llamada',
      elapsedSeconds: session.elapsedSeconds || 0,
      canHangup: true
    } : current);
  }, [startTimer]);

  /**
   * Arranca la grabacion cuando ya estan las dos voces y el call_id de Enigma.
   * Se intenta desde ontrack y al recibir la respuesta del proveedor, porque no
   * siempre llegan en el mismo orden. Si falla, la llamada sigue sin grabar.
   */
  const startRecording = useCallback((session) => {
    if (!session || session.recording || session.ending) return;
    if (!session.answeredAt) return;
    if (!session.enigmaCallId || !session.stream || !session.remoteStream) return;
    if (!recordingSupported()) {
      console.warn('[WA-REC] Este navegador no permite grabar la llamada');
      return;
    }
    try {
      const recording = new WhatsAppCallRecording({
        callId: session.enigmaCallId,
        localStream: session.stream,
        remoteStream: session.remoteStream
      });
      recording.start();
      session.recording = recording;
    } catch (error) {
      console.warn('[WA-REC] No se pudo iniciar la grabacion:', error?.message || error);
    }
  }, []);

  startRecordingRef.current = startRecording;

  const applyStatus = useCallback((session, response) => {
    if (!session || sessionRef.current !== session || session.ending) return;
    const status = normalizeStatus(response?.status);
    if (!status) return;
    if (ANSWERED_STATUSES.has(status)) {
      markAnswered(session, response?.acceptedAt);
      return;
    }
    if (TERMINAL_STATUSES.has(status)) {
      clearSession(session, endLabel(status), status === 'failed');
      return;
    }
    if (!session.answeredAt) {
      setDisplay((current) => current ? { ...current, label: statusLabel(status) } : current);
    }
  }, [clearSession, markAnswered]);

  const recoverStatus = useCallback(async (session) => {
    if (!session || sessionRef.current !== session || session.ending) return;
    if (!session.phoneCallId && !session.callSid) return;
    const response = await whatsappRef.current.getCallStatus({
      locationId: session.locationId,
      ...(session.phoneCallId ? { phoneCallId: session.phoneCallId } : {}),
      ...(session.callSid ? { callSid: session.callSid } : {}),
      ...(session.enigmaCallId ? { enigmaCallId: session.enigmaCallId } : {})
    });
    applyStatus(session, response);
  }, [applyStatus]);

  useEffect(() => subscribeWhatsAppCallEvents((event) => {
    const session = sessionRef.current;
    if (!session || session.ending) return;
    if (event?.type === 'reconnected') {
      recoverStatus(session).catch(() => {});
      return;
    }
    if (event?.type !== 'status') return;
    const payload = event.payload || {};
    const samePhoneCall = session.phoneCallId && payload.phoneCallId === session.phoneCallId;
    const sameSid = session.callSid && payload.callSid === session.callSid;
    if (!samePhoneCall && !sameSid) return;
    applyStatus(session, payload);
    // Una consulta por EVENTO, no por intervalo. Ademas de reconciliar cualquier
    // carrera, hace que el backend registre accepted/terminal en cti_log_calls.
    recoverStatus(session).catch(() => {});
  }), [applyStatus, recoverStatus]);

  const terminate = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || session.ending) return;
    session.ending = true;
    session.cancelRequested = true;
    session.endReason = 'terminate';
    setDisplay((current) => current ? { ...current, phase: 'ending', label: 'Colgando', canHangup: false } : current);
    try {
      const id = session.callSid || session.callId || session.phoneCallId;
      if (id) {
        await whatsappRef.current.terminateCall(session.conversationId, {
          locationId: session.locationId,
          call_id: id,
          enigmaCallId: session.enigmaCallId || undefined,
          phoneNumberId: session.phoneNumberId || undefined
        });
      }
      clearSession(session, 'Llamada finalizada');
    } catch (error) {
      clearSession(session, 'Llamada finalizada');
    }
  }, [clearSession]);

  const start = useCallback(async (conversation) => {
    if (!conversation || sessionRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.RTCPeerConnection) {
      throw new Error('Este navegador no soporta llamadas WebRTC.');
    }

    const attemptId = ++attemptRef.current;
    const session = {
      attemptId,
      conversationId: conversation.id,
      conversationName: conversation.name || 'Contacto',
      locationId: conversation.locationId,
      phone: conversation.phone || '',
      initiatedAt: Date.now(),
      startedAt: null,
      elapsedSeconds: 0,
      ending: false,
      cancelRequested: false,
      // Se prepara aqui, durante el clic, para que el navegador permita el audio.
      ringback: createRingbackTone()
    };
    session.ringback.prepare();
    sessionRef.current = session;
    setDisplay({
      conversationId: session.conversationId,
      conversationName: session.conversationName,
      phone: session.phone,
      phase: 'connecting',
      label: 'Iniciando llamada',
      elapsedSeconds: 0,
      canHangup: true
    });
    try {
      session.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (session.cancelRequested) {
        stopSessionMedia(session);
        return;
      }
      session.peer = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      session.stream.getTracks().forEach((track) => session.peer.addTrack(track, session.stream));
      session.peer.ontrack = (event) => {
        const [remoteStream] = event.streams || [];
        // Chrome solo entrega el audio remoto de WebRTC a WebAudio si tambien se
        // reproduce en un <audio>: por eso la grabacion usa este mismo stream.
        if (audioRef.current && remoteStream) {
          audioRef.current.srcObject = remoteStream;
          audioRef.current.play().catch(() => {});
        }
        if (remoteStream) {
          session.remoteStream = remoteStream;
          startRecording(session);
        }
      };
      session.peer.onconnectionstatechange = () => {
        const state = session.peer?.connectionState;
        if (['failed', 'disconnected'].includes(state) && !session.ending) {
          clearSession(session, state === 'failed' ? 'Llamada fallida' : 'Llamada finalizada', state === 'failed');
        }
      };

      const offer = await session.peer.createOffer({ offerToReceiveAudio: true });
      await session.peer.setLocalDescription(offer);
      await waitForIceGathering(session.peer);
      setDisplay((current) => current ? { ...current, phase: 'ringing', label: 'Llamando' } : current);
      if (!session.cancelRequested && !session.answeredAt) session.ringback?.start();

      const response = await whatsappRef.current.startCall(session.conversationId, {
        locationId: session.locationId,
        sdp: session.peer.localDescription?.sdp || offer.sdp
      });
      const result = response?.result || {};
      session.phoneCallId = phoneCallId(result);
      session.callSid = callSid(result);
      session.callId = session.callSid || session.phoneCallId;
      session.phoneNumberId = result.phoneNumberId || result.phone_number_id || null;
      session.enigmaCallId = response?.enigmaCallId || null;

      if (session.cancelRequested) {
        if (session.callId) {
          whatsappRef.current.terminateCall(session.conversationId, {
            locationId: session.locationId,
            call_id: session.callId,
            enigmaCallId: session.enigmaCallId || undefined,
            phoneNumberId: session.phoneNumberId || undefined
          }).catch(() => {});
        }
        stopSessionMedia(session);
        return;
      }

      const sdp = answerSdp(result);
      if (sdp) {
        await session.peer.setRemoteDescription({ type: 'answer', sdp });
      }
      startRecording(session);
      // Recuperacion unica: los eventos pueden haber llegado mientras el POST
      // esperaba el SDP, antes de que conociéramos phoneCallId/callSid.
      recoverStatus(session).catch(() => {});
    } catch (error) {
      if (!session.cancelRequested) {
        clearSession(session, 'Llamada fallida', true);
        throw error;
      }
      stopSessionMedia(session);
    }
  }, [clearSession, recoverStatus, startRecording, stopSessionMedia]);

  useEffect(() => () => {
    const session = sessionRef.current;
    if (!session) return;
    const id = session.callSid || session.callId || session.phoneCallId;
    if (id) {
      whatsappRef.current.terminateCall(session.conversationId, {
        locationId: session.locationId,
        call_id: id,
        enigmaCallId: session.enigmaCallId || undefined,
        phoneNumberId: session.phoneNumberId || undefined
      }).catch(() => {});
    }
    session.endReason = 'unmount';
    stopSessionMedia(session);
    sessionRef.current = null;
  }, [stopSessionMedia]);

  const value = useMemo(() => ({
    display,
    active: Boolean(display && !['ended', 'failed'].includes(display.phase)),
    start,
    terminate,
    audioRef
  }), [display, start, terminate]);

  return (
    <WhatsAppCallContext.Provider value={value}>
      {children}
      <audio ref={audioRef} autoPlay playsInline hidden />
    </WhatsAppCallContext.Provider>
  );
}

export default function useWhatsAppCall() {
  const context = useContext(WhatsAppCallContext);
  if (!context) throw new Error('useWhatsAppCall must be used within WhatsAppCallProvider');
  return context;
}
